import { Box, Dialog, Stack, Typography } from '@mui/material';

import HiddenDialogButton from '../../../components/Dialog/HiddenDialogButton';
import { RemoveButton } from '../../../components/styledComponents/IconButtons';
import { LinkWithNoRouter as Link } from '../../../components/styledComponents/Link';
import colors from '../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../hooks/useAnalytics';

interface HighlightedTypographyProps {
  text: string;
  hideYellow?: boolean;
}

const HighlightedTypography = ({
  text,
  hideYellow = false,
}: HighlightedTypographyProps) => (
  <Typography
    sx={{
      color: colors.csaaBlack,
      fontWeight: 700,
      p: 0.5,
      fontFamily: 'Cabin',
      fontSize: { mobile: 16, tablet: 18 },
      backgroundColor: hideYellow ? 'initial' : colors.aaaYellow30,
    }}
  >
    {text}
  </Typography>
);

interface PolicyNumberHelperProps {
  open: boolean;
  closeDialog: () => void;
}

const PolicyNumberHelper = ({ open, closeDialog }: PolicyNumberHelperProps) => {
  const { track } = useDirectAccessAnalytics();

  return (
    <Dialog
      aria-labelledby="policyNumberHelperLabel"
      data-testid="forgot-password-dialog"
      open={open}
      onClose={closeDialog}
      sx={{ backdropFilter: 'blur(5px)' }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          margin: 0,
          px: { mobile: 3, tablet: 4 },
          py: { mobile: 3, tablet: 3 },
          position: { mobile: 'fixed', tablet: 'relative' },
          width: { mobile: 1, tablet: 480 },
          height: 'auto',
          maxHeight: 1,
          background: colors.csaaGray,
        },
      }}
    >
      <HiddenDialogButton />
      <Box pb={{ tablet: 2 }} data-testid="PolicyNumberHelper">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            id="policyNumberHelperLabel"
            sx={{
              fontSize: { tablet: 28 },
              fontWeight: 700,
              lineHeight: { tablet: '40px' },
            }}
          >
            Policy number
          </Typography>
          <RemoveButton
            aria-label="Close policy number info dialog"
            data-testid="close-forgot-password-dialog-button"
            onClick={closeDialog}
          />
        </Stack>

        <Stack spacing={{ mobile: 1, tablet: 2 }}>
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontSize: { mobile: 14, tablet: 16 },
              fontWeight: { mobile: 500, tablet: 400 },
              lineHeight: '21px',
              mt: 3,
              color: colors.csaaDark,
            }}
          >
            Here are common formats for your Policy Numbers.
          </Typography>
          <Stack direction="column">
            <Box
              sx={{
                background: colors.csaaWhite,
                p: 2,
                borderRadius: 2,
                color: colors.csaaBlack,
                fontWeight: 700,
              }}
            >
              <Stack direction="row">
                <HighlightedTypography text="CAXX123456789" />
              </Stack>
              <Stack direction="row" sx={{ pb: 1, pt: 2 }}>
                <HighlightedTypography text="ABCD" />
                <HighlightedTypography hideYellow text="-" />
                <HighlightedTypography text="123456789" />
              </Stack>
            </Box>
            <Typography
              sx={{
                color: colors.csaaMid,
                fontWeight: 400,
                fontFamily: 'Cabin',
                fontSize: { mobile: 14, tablet: 16 },
                mt: 1,
              }}
            >
              Don't include the dash.
            </Typography>
          </Stack>
          <Typography
            sx={{
              color: colors.csaaMid,
              fontWeight: 700,
              fontSize: { mobile: 16, tablet: 18 },
              mt: { tablet: 3 },
            }}
          >
            Need help? Call{' '}
            <Link
              href="tel:+18009228228"
              onClick={() =>
                track('Contact Accessed', {
                  event_type: 'Link Accessed',
                  link: '+18009228228',
                  method: 'Call',
                  contents: 'Customer Support 24/7 (CA, NV, UT)',
                  dialed_number: '18009228228',
                  location: 'Modal',
                })
              }
            >
              800.922.8228
            </Link>{' '}
            (CA, NV, UT)
          </Typography>
          <Typography
            sx={{
              color: colors.csaaMid,
              fontWeight: 700,
              fontSize: { mobile: 16, tablet: 18 },
            }}
          >
            or{' '}
            <Link
              href="tel:+18002073618"
              onClick={() =>
                track('Contact Accessed', {
                  event_type: 'Link Accessed',
                  link: '+18002073618',
                  method: 'Call',
                  contents: 'Customer Support 24/7 (states except CA, NV, UT)',
                  dialed_number: '18002073618',
                  location: 'Modal',
                })
              }
            >
              800.207.3618
            </Link>{' '}
            (all other states)
          </Typography>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default PolicyNumberHelper;
